import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { auth } from './firebase';
import { logger } from './logger';

// Create a base API instance with common configuration
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token when available
api.interceptors.request.use(
  async config => {
    try {
      const user = auth.currentUser;
      logger.log('Request interceptor - user exists:', !!user);

      if (user) {
        // Do not force-refresh — Firebase SDK auto-refreshes before expiry.
        // getIdToken(true) makes a network round-trip on every request,
        // and if that call fails the request goes out unauthenticated.
        const token = await user.getIdToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    } catch (error) {
      logger.error('Error adding auth token to request:', error);
      return config;
    }
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Handle specific HTTP error codes
      switch (error.response.status) {
        case 401: // Unauthorized
          logger.warn('Authentication required for this request');
          // You could trigger a re-authentication flow here
          break;
        case 403: // Forbidden
          logger.warn('You do not have permission to access this resource');
          break;
        case 404: // Not Found
          logger.warn('Resource not found');
          break;
        case 500: // Server Error
          logger.error('Server error occurred');
          break;
        default:
          logger.error(`Request failed with status: ${error.response.status}`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      logger.error('No response received from server');
    } else {
      // Something happened in setting up the request
      logger.error('Error setting up request:', error.message);
    }

    return Promise.reject(error);
  }
);

// Helper function to make GET requests with query parameters
export const fetchData = async <T>(
  endpoint: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await api.get<T>(endpoint, {
    params,
    ...config,
  });
  return response.data;
};

// Helper function to create a resource
export const createResource = async <T, R = T>(
  endpoint: string,
  data: R,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await api.post<T>(endpoint, data, config);
  return response.data;
};

// Helper function to update a resource
export const updateResource = async <T, R = Partial<T>>(
  endpoint: string,
  data: R,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await api.patch<T>(endpoint, data, config);
  return response.data;
};

// Helper function to delete a resource
export const deleteResource = async (
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<void> => {
  await api.delete(endpoint, config);
};

// Export the configured axios instance
export default api;
